<template>
    <div class="editor-menubar">
        <template v-if="visibleItems.length">
            <div v-for="item in visibleItems" :key="item.id" class="editor-menubar__item">
                <MenuItem :item="item" />
            </div>
        </template>
        <Dropdown v-if="collapsedItems.length" class="editor-menubar__collapsed-menu">
            <template #label>
                <div class="editor-menubar__item">
                    <IconDots fill="rgb(127, 127, 127)" class="editor-menubar__icon" />
                </div>
            </template>
            <template #items>
                <li v-for="item in collapsedItems" :key="item.id" class="dropdown__item">
                    <MenuItem :item="item" />
                    <Switcher
                        v-if="item.switcher"
                        :model-value="item.switcher.value"
                        @update:model-value="item.switcher.handler"
                    />
                </li>
            </template>
        </Dropdown>
    </div>
</template>

<script>
import Dropdown from '@speechki-frontend/shared/ui/Dropdown';
import Switcher from '@speechki-frontend/shared/ui/Switcher';
import { IconDots } from '@speechki-frontend/shared/ui/icons';
import MenuItem from './MenuItem';

const OPTIONS = {
    default: {
        showLabel: false,
        showIcon: true
    },
    collapsed: {
        showLabel: true,
        showIcon: true
    }
};

export default {
    name: 'EditorMenubar',
    components: {
        Dropdown,
        Switcher,
        IconDots,
        MenuItem
    },
    inject: ['isEmbed'],
    props: {
        items: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        computedItems() {
            if (!this.items.length) return [];
            return this.items
                .filter(
                    ({ embed = null, filter = () => true }) =>
                        (!this.isEmbed || !!embed) && filter()
                )
                .map(item => {
                    const {
                        name: id,
                        embed = null,
                        icon = null,
                        params = {},
                        query = {},
                        options = {}
                    } = item;
                    const name = this.isEmbed && !!embed ? embed : item.name;
                    // some items have no label; vue-i18n 9 throws on $t(undefined)
                    const label = item.label ? this.$t(item.label) : '';
                    let switcher = undefined;
                    let switcherKey = undefined;

                    if (item.switcher) {
                        switcherKey = `menuBarItem-${id}`;
                        switcher = {
                            value: JSON.parse(localStorage.getItem(switcherKey)) || false,
                            handler: value => {
                                localStorage.setItem(switcherKey, value);
                                window.dispatchEvent(
                                    new CustomEvent(`${switcherKey}-switched`, {
                                        detail: {
                                            action: 'set',
                                            key: switcherKey,
                                            content: value
                                        }
                                    })
                                );
                            }
                        };
                    }

                    if ('childs' in item) {
                        item.childs = item.childs.map(child => ({
                            ...child,
                            label: child.label ? this.$t(child.label) : '',
                            to: {
                                name: this.isEmbed && !!child.embed ? child.embed : child.name,
                                params: child.params || {},
                                query: child.query || {}
                            }
                        }));
                    }

                    return {
                        ...item,
                        id,
                        label,
                        icon,
                        options,
                        to: { name, params, query },
                        classes: ['editor-menubar__link', { 'editor-menubar__link--icon': !!icon }],
                        switcher
                    };
                });
        },
        visibleItems() {
            return this.computedItems
                .filter(({ collapsed = false }) => !collapsed)
                .map(item => ({ ...item, options: { ...OPTIONS.default, ...item.options } }));
        },
        collapsedItems() {
            return this.computedItems
                .filter(({ collapsed = false }) => collapsed)
                .map(item => ({ ...item, options: { ...OPTIONS.collapsed, ...item.options } }));
        }
    }
};
</script>

<style lang="scss" src="./EditorMenubar.scss"></style>
